import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useLingui } from '@lingui/react/macro';
import { AuthContext } from '../contexts/AuthContext';
import { onboardingService } from '../services/onboardingService';

// --- Types --------------------------------------------------------------------

// EPIC-15 (PLG): the payment step is gone — onboarding never touches a card.
// Pro is free; Max is bought from the plan step at signup or any upsell
// surface later.
type Step = 1|2|3|4|5|6|7;
const TOTAL = 7;

const PHASES = [
  { steps: [1,2,3] },
  { steps: [4,5]   },
  { steps: [6,7] },
];

function phaseOf(s: number) {
  return PHASES.findIndex(p => p.steps.includes(s));
}

// --- SVG Icon Library ---------------------------------------------------------

const Icon = {
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.25L3.75 6v6c0 5.25 3.75 10.15 8.25 11.25C16.5 22.15 20.25 17.25 20.25 12V6L12 2.25z"/>
    </svg>
  ),
  AlertTriangle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  Layers: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
    </svg>
  ),
  Zap: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  Code: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  Lock: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  ),
  Send: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  ChevronLeft: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  ),
};

const STEP_ICONS: Record<number, JSX.Element> = {
  1: <Icon.Shield />,
  2: <Icon.AlertTriangle />,
  3: <Icon.Layers />,
  4: <Icon.Zap />,
  5: <Icon.Code />,
  6: <Icon.Shield />,
  7: <Icon.Lock />,
};

// --- Design tokens ------------------------------------------------------------

const C = {
  bg:       '#09090b',
  card:     'rgba(255,255,255,0.03)',
  border:   'rgba(255,255,255,0.07)',
  borderHi: 'rgba(255,255,255,0.14)',
  accent:   '#6366f1',
  accentDim:'rgba(99,102,241,0.15)',
  success:  '#10b981',
  text:     '#f8fafc',
  sub:      'rgba(248,250,252,0.55)',
  muted:    'rgba(248,250,252,0.3)',
};

const inputSt: React.CSSProperties = {
  padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.border}`,
  background: 'rgba(0,0,0,0.25)', color: C.text, fontSize: '0.95rem',
  outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%', boxSizing: 'border-box',
};

// --- Main Page ----------------------------------------------------------------


// Default demo example text — carefully chosen to showcase real Privacy Twins correctly
const DEFAULT_DEMO_TEXT =
  "Hi, I'm Marcus Webb and my work email is marcus.webb@stratford-legal.com. " +
  "Please wire the retainer to account 7823-991-04 and my SSN is 523-91-4417.";

export default function OnboardingPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { user, refreshUser } = useContext(AuthContext);

  const [step, setStep] = useState<Step>(1);

  // Step 1
  const [palName, setPalName] = useState('');

  // Step 2
  const [leakSealed, setLeakSealed] = useState(false);

  // Step 4 — demo
  const [demoInput, setDemoInput] = useState('');
  const [demoResult, setDemoResult] = useState<{
    masked: string;
    entityTypes: string[];
    twins: Array<{ original: string; twin: string; entityType: string }>;
  } | null>(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');

  // Step 5 — how do you work
  const [devMode, setDevMode] = useState<'dev' | 'user' | null>(null);

  // Step 7 — activation
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  // Guard: redirect away if onboarding already completed
  useEffect(() => {
    onboardingService.getStatus().then(done => {
      if (done) navigate('/dashboard', { replace: true });
    });
  }, [navigate]);


  // Nav
  const next = () => setStep(s => Math.min(s + 1, TOTAL) as Step);
  const back = () => setStep(s => Math.max(s - 1, 1) as Step);

  // Demo
  const runDemo = async () => {
    const text = demoInput.trim() || DEFAULT_DEMO_TEXT;
    setDemoInput(text);
    setDemoResult(null);
    setDemoError('');
    setDemoLoading(true);
    try {
      const result = await onboardingService.demoEncode(text);
      setDemoResult(result);
    } catch (err: any) {
      setDemoError(err.message || t`Analysis failed`);
    } finally {
      setDemoLoading(false);
    }
  };

  // Activate
  const activate = async () => {
    setActivating(true);
    try {
      await onboardingService.complete({
        palName: palName || user?.firstName || 'Pal',
        crownJewelData: '',
        sensitivityLevel: 65,
        integrations: [],
        conversationSummary: '',
      });
      await refreshUser();
      setTimeout(() => {
        setActivating(false);
        setActivated(true);
        // Redirect to dashboard after brief success display
        setTimeout(() => navigate('/dashboard', { replace: true }), 1800);
      }, 1800);
    } catch (e) {
      console.error(e);
      setActivating(false);
    }
  };

  // Phase / progress
  const phaseIdx = phaseOf(step);
  const phaseLabels = [t`Welcome`, t`See it Work`, t`You're In`];
  const phaseLabel = phaseLabels[phaseIdx] ?? '';
  const palDisplayName = palName || user?.firstName || t`Your Pal`;
  const progress = ((step - 1) / (TOTAL - 1)) * 100;

  return (
    <>
      <style>{CSS}</style>
      <div className="ob-root">
        <div className="ob-bg" />

        {/* Top progress bar */}
        <div className="ob-topbar">
          <div className="ob-topbar-fill" style={{ width: `${progress}%` }} />
        </div>

        {/* Nav */}
        <nav className="ob-nav">
          <img src="/privacypal-logo-light.png" alt="PrivacyPal" style={{ height: 48 }} />
          <div className="ob-step-label">
            <span style={{ color: C.accent, fontWeight: 600 }}>{phaseLabel}</span>
            <span style={{ color: C.muted, marginLeft: '0.75rem' }}><Trans>Step {step} of {TOTAL}</Trans></span>
          </div>
        </nav>

        {/* Main */}
        <main className="ob-main">
          <div className="ob-card" key={step}>

            {/* Step header */}
            <div className="ob-step-header">
              <div className="ob-step-icon">{STEP_ICONS[step]}</div>
            </div>

            {/* ----------------------------------------------------------
                STEP 1 — Welcome
            ---------------------------------------------------------- */}
            {step === 1 && (
              <div className="ob-step ob-fadein">
                <h1 className="ob-h1"><Trans>Meet your PrivacyPal.</Trans></h1>
                <p className="ob-body">
                  <Trans>Think of me as a dedicated member of your team: one who works around the clock to make sure your sensitive data never ends up where it shouldn't. Ready to get started?</Trans>
                </p>
                <p className="ob-body" style={{ marginTop: '0.25rem', color: C.muted }}>
                  <Trans>What should I call you? (We'll use your first name if you skip this.)</Trans>
                </p>
                <input
                  style={{ ...inputSt, marginTop: '1rem', fontSize: '1rem' }}
                  placeholder={user?.firstName || t`Your name`}
                  value={palName}
                  onChange={e => setPalName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && next()}
                  autoFocus
                />
                <div className="ob-actions">
                  <button className="ob-btn-primary" onClick={next}>
                    <Trans>Let's go</Trans> <div style={{ width: 16 }}><Icon.ArrowRight /></div>
                  </button>
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------
                STEP 2 — The Problem
            ---------------------------------------------------------- */}
            {step === 2 && (
              <div className="ob-step ob-fadein">
                <h1 className="ob-h1"><Trans>Your data is leaking, silently.</Trans></h1>
                <p className="ob-body">
                  <Trans>Every time your team uses an LLM (ChatGPT, Copilot, Claude), a piece of your IP, your client data, your trade secrets quietly walks out the door.</Trans>
                </p>

                <div className="ob-leak-visual">
                  {!leakSealed ? (
                    <>
                      <div className="ob-leak-row">
                        {['Slack', 'Gmail', 'ChatGPT', 'Copilot'].map(app => (
                          <div key={app} className="ob-leak-chip">
                            <span>{app}</span>
                            <div className="ob-leak-drop" />
                          </div>
                        ))}
                      </div>
                      <div className="ob-leak-arrow">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(248,113,113,0.6)" strokeWidth={2}><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                      </div>
                      <div className="ob-leak-sink"><Trans>Unprotected AI: your data exposed</Trans></div>
                    </>
                  ) : (
                    <div className="ob-sealed">
                      <div style={{ width: 32, height: 32, color: C.success }}><Icon.Lock /></div>
                      <span><Trans>Leak sealed. PrivacyPal is on the job.</Trans></span>
                    </div>
                  )}
                </div>

                <div className="ob-actions">
                  <button className="ob-btn-ghost" onClick={back}><div style={{ width: 16 }}><Icon.ChevronLeft /></div> <Trans>Back</Trans></button>
                  {!leakSealed
                    ? <button className="ob-btn-primary" onClick={() => setLeakSealed(true)}><Trans>Seal the leak</Trans></button>
                    : <button className="ob-btn-primary" onClick={next}><Trans>Continue</Trans> <div style={{ width: 16 }}><Icon.ArrowRight /></div></button>
                  }
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------
                STEP 3 — Privacy Engineering
            ---------------------------------------------------------- */}
            {step === 3 && (
              <div className="ob-step ob-fadein">
                <h1 className="ob-h1"><Trans>A new discipline: Privacy Engineering.</Trans></h1>
                <p className="ob-body">
                  <Trans>Compliance checklists are reactive. Privacy Engineering is proactive: automated agents embedded across your stack that intercept sensitive data before it ever reaches an AI.</Trans>
                </p>

                <div className="ob-blueprint-on ob-fadein">
                  {[
                    { label: t`Your Apps`, sub: t`Slack, Gmail, code editors, ChatGPT...` },
                    { label: t`PrivacyPal Layer`, sub: t`Real-time interception & masking with Privacy Twins`, accent: true },
                    { label: t`AI Services`, sub: t`Receive clean, safe data only` },
                  ].map((row, i) => (
                    <div key={i} className={`ob-arch-row ${row.accent ? 'accent' : ''}`}>
                      <div className="ob-arch-label">{row.label}</div>
                      <div className="ob-arch-sub">{row.sub}</div>
                    </div>
                  ))}
                </div>

                <div className="ob-actions">
                  <button className="ob-btn-ghost" onClick={back}><div style={{ width: 16 }}><Icon.ChevronLeft /></div> <Trans>Back</Trans></button>
                  <button className="ob-btn-primary" onClick={next}><Trans>I'm ready to see it work</Trans> <div style={{ width: 16 }}><Icon.ArrowRight /></div></button>
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------
                STEP 4 — Live Demo (real Privacy Twins)
            ---------------------------------------------------------- */}
            {step === 4 && (
              <div className="ob-step ob-fadein">
                <h1 className="ob-h1"><Trans>Watch PrivacyPal work.</Trans></h1>
                <p className="ob-body">
                  <Trans>Type anything containing sensitive data below. PrivacyPal replaces each piece of real PII with a realistic Privacy Twin: indistinguishable to the AI, meaningless to an attacker.</Trans>
                </p>

                <div className="ob-demo-grid">
                  <div className="ob-demo-col">
                    <div className="ob-demo-col-label"><Trans>Your input</Trans></div>
                    <textarea
                      className="ob-demo-textarea"
                      placeholder={DEFAULT_DEMO_TEXT}
                      value={demoInput}
                      onChange={e => { setDemoInput(e.target.value); setDemoResult(null); setDemoError(''); }}
                      rows={5}
                    />
                  </div>
                  <div className="ob-demo-divider">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth={2}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </div>
                  <div className="ob-demo-col">
                    <div className="ob-demo-col-label" style={{ color: C.success }}><Trans>Protected output</Trans></div>
                    <div className="ob-demo-output">
                      {demoLoading && <div className="ob-demo-loading"><div className="ob-spinner-sm"/><span><Trans>Generating Privacy Twins...</Trans></span></div>}
                      {demoError && <span style={{ color: '#f87171', fontSize: '0.85rem' }}>{demoError}</span>}
                      {demoResult && (
                        <>
                          <div className="ob-demo-masked">{demoResult.masked}</div>
                          {demoResult.entityTypes.length > 0 && (
                            <div className="ob-demo-tags">
                              {demoResult.entityTypes.map(t => (
                                <span key={t} className="ob-demo-tag">{t}</span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      {!demoLoading && !demoError && !demoResult && (
                        <span style={{ color: C.muted, fontSize: '0.85rem' }}><Trans>Protected text will appear here</Trans></span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Privacy Twins table */}
                {demoResult && demoResult.twins.length > 0 && (
                  <div className="ob-twins-table ob-fadein">
                    <div className="ob-twins-header">
                      <span><Trans>Real Value</Trans></span>
                      <span><Trans>Privacy Twin</Trans></span>
                      <span><Trans>Type</Trans></span>
                    </div>
                    {demoResult.twins.map((t, i) => (
                      <div key={i} className="ob-twins-row">
                        <span className="ob-twins-original">{t.original}</span>
                        <span className="ob-twins-twin">{t.twin}</span>
                        <span className="ob-twins-type">{t.entityType}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="ob-actions">
                  <button className="ob-btn-ghost" onClick={back}><div style={{ width: 16 }}><Icon.ChevronLeft /></div> <Trans>Back</Trans></button>
                  <button className="ob-btn-ghost" onClick={runDemo} disabled={demoLoading}>
                    <div style={{ width: 16 }}><Icon.Zap /></div>
                    {demoLoading ? <Trans>Generating...</Trans> : <Trans>Run Protection</Trans>}
                  </button>
                  {demoResult && (
                    <button className="ob-btn-primary" onClick={next}>
                      <Trans>Continue</Trans> <div style={{ width: 16 }}><Icon.ArrowRight /></div>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------
                STEP 5 — How do you work?
            ---------------------------------------------------------- */}
            {step === 5 && (
              <div className="ob-step ob-fadein">
                <h1 className="ob-h1"><Trans>How do you work?</Trans></h1>
                <p className="ob-body"><Trans>PrivacyPal fits into your workflow, whether you're building integrations or simply want it to run quietly in the background.</Trans></p>

                <div className="ob-mode-row">
                  {[
                    { id: 'dev',  title: t`Developer`,     sub: t`SDK, API & CI/CD integrations`, icon: <Icon.Code /> },
                    { id: 'user', title: t`Non-technical`, sub: t`Proxy app & browser extension`,  icon: <Icon.Shield /> },
                  ].map(m => (
                    <button
                      key={m.id}
                      className={`ob-mode-card ${devMode === m.id ? 'selected' : ''}`}
                      onClick={() => setDevMode(m.id as 'dev' | 'user')}
                    >
                      <div className="ob-mode-icon">{m.icon}</div>
                      <div className="ob-mode-title">{m.title}</div>
                      <div className="ob-mode-sub">{m.sub}</div>
                    </button>
                  ))}
                </div>

                {devMode === 'dev' && (
                  <div className="ob-code-block ob-fadein">
                    <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '0.5rem', letterSpacing: '0.05em' }}><Trans>QUICK START</Trans></div>
                    <code>npm install @privacypal/sdk</code>
                    <br/>
                    <code style={{ color: '#a5f3fc' }}>import {'{ PrivacyPal }'} from '@privacypal/sdk'</code>
                    <br/>
                    <code style={{ color: '#86efac' }}>const pal = new PrivacyPal({'{ apiKey }'})</code>
                  </div>
                )}
                {devMode === 'user' && (
                  <div className="ob-info-box ob-fadein">
                    <Trans>Your PrivacyPal desktop app is already configured. Once you complete setup, it will silently route all AI traffic through your personal protection layer.</Trans>
                  </div>
                )}

                <div className="ob-actions">
                  <button className="ob-btn-ghost" onClick={back}><div style={{ width: 16 }}><Icon.ChevronLeft /></div> <Trans>Back</Trans></button>
                  <button className="ob-btn-primary" onClick={next} disabled={!devMode}>
                    {devMode ? <Trans>Continue</Trans> : <Trans>Select one to continue</Trans>} {devMode && <div style={{ width: 16 }}><Icon.ArrowRight /></div>}
                  </button>
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------
                STEP 6 — Promise
            ---------------------------------------------------------- */}
            {step === 6 && (
              <div className="ob-step ob-fadein" style={{ alignItems: 'center', textAlign: 'center' }}>
                <div className="ob-promise-icon"><Icon.Shield /></div>
                <h1 className="ob-h1"><Trans>PrivacyPal doesn't sleep.</Trans></h1>
                <p className="ob-body" style={{ maxWidth: 440 }}>
                  <Trans>{palDisplayName} runs 24 hours a day, seven days a week: silently intercepting, masking, and protecting. You focus on building. Leave the risk management to us.</Trans>
                </p>
                <div className="ob-promise-tags">
                  {[t`Zero data leaks`, t`24/7 protection`, t`AI-native privacy`, t`No lock-in`].map(tag => (
                    <span key={tag} className="ob-promise-tag">{tag}</span>
                  ))}
                </div>
                <div className="ob-actions" style={{ justifyContent: 'center' }}>
                  <button className="ob-btn-ghost" onClick={back}><div style={{ width: 16 }}><Icon.ChevronLeft /></div> <Trans>Back</Trans></button>
                  <button className="ob-btn-primary" onClick={next}>
                    <Trans>Let's do this</Trans> <div style={{ width: 16 }}><Icon.ArrowRight /></div>
                  </button>
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------
                STEP 7 — Activation (no payment step exists: Pro is free)
            ---------------------------------------------------------- */}
            {step === 7 && (
              <div className="ob-step ob-fadein" style={{ alignItems: 'center', textAlign: 'center' }}>
                {!activated ? (
                  <>
                    <div className={`ob-activate-icon ${activating ? 'pulsing' : ''}`}><Icon.Lock /></div>
                    <h1 className="ob-h1">{activating ? <Trans>Bringing PrivacyPal online...</Trans> : <Trans>Ready to activate.</Trans>}</h1>
                    <p className="ob-body" style={{ maxWidth: 400 }}>
                      {activating
                        ? <Trans>Configuring your protection layer. This takes just a moment.</Trans>
                        : <Trans>Your Privacy Agent is configured and waiting. Hit activate to go live.</Trans>}
                    </p>
                    {!activating && (
                      <p className="ob-body" style={{ maxWidth: 420, fontSize: '0.85rem', opacity: 0.75 }}>
                        <Trans>You are on PrivacyPal Pro, free forever: ChatGPT, Claude, Gemini, Grok and Perplexity chats protected. Agent surfaces and team features come with Max, whenever you want them.</Trans>
                      </p>
                    )}
                    {!activating && (
                      <button className="ob-btn-primary ob-btn-xl" onClick={activate}>
                        <Trans>Activate PrivacyPal</Trans> <div style={{ width: 18 }}><Icon.ArrowRight /></div>
                      </button>
                    )}
                    {activating && <div className="ob-spinner" style={{ margin: '1.5rem auto 0' }} />}
                    {!activating && (
                      <button className="ob-btn-ghost" onClick={back} style={{ marginTop: '0.75rem' }}>
                        <div style={{ width: 16 }}><Icon.ChevronLeft /></div> <Trans>Back</Trans>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="ob-activate-icon success"><Icon.CheckCircle /></div>
                    <h1 className="ob-h1" style={{ color: C.success }}><Trans>You're a Privacy Engineer.</Trans></h1>
                    <p className="ob-body" style={{ maxWidth: 420 }}>
                      <Trans>PrivacyPal is live. Taking you to your dashboard&hellip;</Trans>
                    </p>
                    <div className="ob-spinner" style={{ margin: '1.5rem auto 0' }} />
                  </>
                )}
              </div>
            )}

          </div>
        </main>
      </div>
    </>
  );
}

// --- CSS ----------------------------------------------------------------------

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  @keyframes fadein    { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse     { 0%,100% { transform:scale(1); } 50% { transform:scale(1.08); } }
  @keyframes spin      { to { transform:rotate(360deg); } }
  @keyframes leakdrop  { 0%{transform:translateY(0);opacity:0.9;} 100%{transform:translateY(24px);opacity:0;} }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ob-root {
    min-height: 100vh;
    background: #09090b;
    color: #f8fafc;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .ob-bg {
    position: fixed; inset: 0; z-index: 0; pointer-events: none;
    background: radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,102,241,0.12) 0%, transparent 60%),
                radial-gradient(ellipse 40% 30% at 90% 90%, rgba(16,185,129,0.06) 0%, transparent 60%);
  }
  .ob-fadein { animation: fadein 0.35s ease both; }

  /* Top progress */
  .ob-topbar {
    position: fixed; top: 0; left: 0; right: 0; height: 2px; background: rgba(255,255,255,0.06); z-index: 100;
  }
  .ob-topbar-fill {
    height: 100%; background: linear-gradient(90deg, #6366f1, #818cf8); transition: width 0.5s ease; border-radius: 0 2px 2px 0;
  }

  /* Nav */
  .ob-nav {
    position: sticky; top: 2px; z-index: 50;
    display: flex; align-items: center; justify-content: space-between;
    padding: 1.1rem 2.5rem;
    background: rgba(9,9,11,0.85); backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .ob-step-label { font-size: 0.85rem; font-weight: 500; }

  /* Main */
  .ob-main {
    position: relative; z-index: 10;
    min-height: calc(100vh - 66px);
    display: flex; align-items: center; justify-content: center;
    padding: 2.5rem 1.5rem 5rem;
  }
  .ob-card {
    width: 100%; max-width: 720px;
    background: rgba(255,255,255,0.028);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 20px;
    padding: 2.75rem 3rem;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 80px -20px rgba(0,0,0,0.6);
  }
  @media(max-width:600px){ .ob-card { padding: 2rem 1.5rem; } }

  /* Step header */
  .ob-step-header { margin-bottom: 1.5rem; }
  .ob-step-icon {
    width: 40px; height: 40px; color: #6366f1;
    display: flex; align-items: center; justify-content: center;
  }
  .ob-step-icon svg { width: 100%; height: 100%; }

  /* Typography */
  .ob-h1 {
    font-size: clamp(1.6rem, 3vw, 2.2rem);
    font-weight: 800; line-height: 1.15;
    letter-spacing: -0.03em;
    color: #f8fafc;
    margin-bottom: 0.9rem;
  }
  .ob-body {
    font-size: 1rem; line-height: 1.7;
    color: rgba(248,250,252,0.55);
    margin-bottom: 0.5rem;
  }

  /* Step layout */
  .ob-step { display: flex; flex-direction: column; width: 100%; }

  /* Actions row */
  .ob-actions {
    display: flex; gap: 0.75rem; margin-top: 2rem; flex-wrap: wrap;
  }
  .ob-btn-primary {
    display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
    padding: 13px 22px; border-radius: 10px; border: none; cursor: pointer;
    background: #6366f1; color: #fff;
    font-weight: 600; font-size: 0.95rem; letter-spacing: -0.01em;
    transition: all 0.2s ease; font-family: inherit;
  }
  .ob-btn-primary:hover:not(:disabled) { background: #818cf8; transform: translateY(-1px); }
  .ob-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .ob-btn-primary.ob-btn-xl { padding: 16px 32px; font-size: 1.05rem; border-radius: 12px; }
  .ob-btn-primary svg, .ob-btn-ghost svg { width: 100%; height: 100%; }
  .ob-btn-ghost {
    display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
    padding: 13px 22px; border-radius: 10px; cursor: pointer;
    background: rgba(255,255,255,0.045); color: rgba(248,250,252,0.6);
    border: 1px solid rgba(255,255,255,0.08); font-weight: 500; font-size: 0.95rem;
    transition: all 0.2s ease; font-family: inherit;
  }
  .ob-btn-ghost:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
  .ob-btn-ghost:disabled { opacity: 0.45; cursor: not-allowed; }

  /* Step 2 — Leak */
  .ob-leak-visual {
    margin: 1.5rem 0; padding: 1.5rem;
    background: rgba(248,113,113,0.04); border: 1px solid rgba(248,113,113,0.15);
    border-radius: 14px;
  }
  .ob-leak-row { display: flex; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .ob-leak-chip {
    display: flex; flex-direction: column; align-items: center; gap: 0.25rem;
    padding: 0.6rem 1rem;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px; font-size: 0.85rem; font-weight: 500; position: relative;
  }
  .ob-leak-drop {
    width: 6px; height: 6px; border-radius: 50%;
    background: rgba(248,113,113,0.8);
    animation: leakdrop 1.1s ease-in infinite;
  }
  .ob-leak-arrow { display: flex; justify-content: center; margin-bottom: 0.75rem; }
  .ob-leak-sink {
    text-align: center; font-size: 0.82rem; color: rgba(248,113,113,0.7);
    padding: 0.5rem; border: 1px dashed rgba(248,113,113,0.25); border-radius: 8px;
  }
  .ob-sealed {
    display: flex; align-items: center; justify-content: center; gap: 0.75rem;
    color: #10b981; font-weight: 600; font-size: 0.95rem; animation: fadein 0.4s ease;
  }

  /* Step 3 — Blueprint */
  .ob-blueprint-on {
    margin: 1.5rem 0; display: flex; flex-direction: column; gap: 2px;
  }
  .ob-arch-row {
    padding: 0.9rem 1.25rem; border-radius: 10px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
  }
  .ob-arch-row.accent {
    background: rgba(99,102,241,0.1); border-color: rgba(99,102,241,0.3);
  }
  .ob-arch-label { font-weight: 600; font-size: 0.9rem; margin-bottom: 0.2rem; }
  .ob-arch-sub { font-size: 0.8rem; color: rgba(248,250,252,0.45); }

  /* Step 4 — Demo */
  .ob-demo-grid {
    display: flex; gap: 1rem; margin: 1.5rem 0; align-items: flex-start; flex-wrap: wrap;
  }
  .ob-demo-col { flex: 1; min-width: 180px; display: flex; flex-direction: column; gap: 0.5rem; }
  .ob-demo-col-label {
    font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.08em; color: rgba(248,250,252,0.35);
  }
  .ob-demo-textarea {
    width: 100%; padding: 0.875rem 1rem; border-radius: 10px;
    background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08);
    color: #f8fafc; font-size: 0.875rem; font-family: inherit;
    resize: vertical; outline: none; line-height: 1.55;
  }
  .ob-demo-textarea:focus { border-color: rgba(99,102,241,0.4); }
  .ob-demo-output {
    min-height: 120px; padding: 0.875rem 1rem; border-radius: 10px;
    background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08);
    font-size: 0.875rem; font-family: 'JetBrains Mono', 'Fira Code', monospace;
    line-height: 1.6; color: #10b981; word-break: break-word;
  }
  .ob-demo-divider {
    display: flex; align-items: center; padding-top: 1.4rem; flex-shrink: 0;
  }
  .ob-demo-masked { color: #10b981; margin-bottom: 0.75rem; }
  .ob-demo-tags { display: flex; gap: 0.35rem; flex-wrap: wrap; }
  .ob-demo-tag {
    padding: 2px 8px; border-radius: 4px; font-size: 0.7rem; font-weight: 600;
    background: rgba(16,185,129,0.12); border: 1px solid rgba(16,185,129,0.25);
    color: #10b981; font-family: monospace; letter-spacing: 0.03em;
  }
  .ob-demo-loading { display: flex; align-items: center; gap: 0.5rem; color: rgba(248,250,252,0.4); }
  .ob-spinner-sm {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.1); border-top-color: #6366f1;
    animation: spin 0.7s linear infinite; flex-shrink: 0;
  }

  /* Privacy Twins table */
  .ob-twins-table {
    margin-top: 1.25rem;
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 10px;
    overflow: hidden;
    font-size: 0.8rem;
  }
  .ob-twins-header {
    display: grid; grid-template-columns: 1fr 1fr auto;
    padding: 0.5rem 1rem;
    background: rgba(99,102,241,0.12);
    font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.08em; color: rgba(248,250,252,0.4);
    gap: 0.75rem;
  }
  .ob-twins-row {
    display: grid; grid-template-columns: 1fr 1fr auto;
    padding: 0.5rem 1rem; gap: 0.75rem;
    border-top: 1px solid rgba(255,255,255,0.05);
    align-items: center;
  }
  .ob-twins-row:nth-child(even) { background: rgba(255,255,255,0.015); }
  .ob-twins-original { color: rgba(248,113,113,0.8); font-family: monospace; }
  .ob-twins-twin { color: #10b981; font-family: monospace; }
  .ob-twins-type {
    padding: 2px 6px; border-radius: 4px; font-size: 0.65rem; font-weight: 600;
    background: rgba(99,102,241,0.15); color: #818cf8;
    font-family: monospace; white-space: nowrap;
  }

  /* Step 5 */
  .ob-mode-row { display: flex; gap: 1rem; margin: 1.5rem 0; flex-wrap: wrap; }
  .ob-mode-card {
    flex: 1; min-width: 160px; padding: 1.25rem 1rem;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    border-radius: 14px; cursor: pointer; text-align: left; transition: all 0.2s;
    font-family: inherit; color: #f8fafc;
  }
  .ob-mode-card:hover { border-color: rgba(99,102,241,0.4); background: rgba(99,102,241,0.05); }
  .ob-mode-card.selected { border-color: #6366f1; background: rgba(99,102,241,0.1); }
  .ob-mode-icon { width: 22px; height: 22px; color: #6366f1; margin-bottom: 0.625rem; }
  .ob-mode-icon svg { width: 100%; height: 100%; }
  .ob-mode-title { font-weight: 700; font-size: 0.95rem; margin-bottom: 0.25rem; }
  .ob-mode-sub { font-size: 0.8rem; color: rgba(248,250,252,0.4); }
  .ob-code-block {
    padding: 1.1rem 1.25rem; border-radius: 12px;
    background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.07);
    font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 0.82rem;
    line-height: 2; color: #f8fafc; margin-bottom: 0.5rem;
  }
  .ob-info-box {
    padding: 1rem 1.25rem; border-radius: 12px;
    background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.2);
    font-size: 0.875rem; line-height: 1.6; color: rgba(248,250,252,0.6);
    margin-bottom: 0.5rem;
  }

  /* Step 6 — Promise */
  .ob-promise-icon {
    width: 56px; height: 56px; margin: 0 auto 1.25rem;
    color: #6366f1; padding: 14px;
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);
    border-radius: 16px;
  }
  .ob-promise-icon svg { width: 100%; height: 100%; }
  .ob-promise-tags { display: flex; flex-wrap: wrap; gap: 0.5rem; justify-content: center; margin: 1.5rem 0; }
  .ob-promise-tag {
    padding: 5px 14px; border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.1); font-size: 0.8rem;
    color: rgba(248,250,252,0.55); font-weight: 500;
  }

  /* Step 7 — success box */
  .ob-success-box {
    display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
    padding: 1rem 1.25rem; border-radius: 12px;
    background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2);
    font-size: 0.9rem; color: #10b981; font-weight: 500; margin-top: 1rem;
  }
  .ob-success-box svg { width: 100%; height: 100%; }

  /* Step 8 — Activate */
  .ob-activate-icon {
    width: 60px; height: 60px; margin: 0 auto 1.25rem;
    color: #6366f1; padding: 14px;
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);
    border-radius: 18px;
  }
  .ob-activate-icon.success { color: #10b981; background: rgba(16,185,129,0.1); border-color: rgba(16,185,129,0.2); }
  .ob-activate-icon.pulsing { animation: pulse 1s infinite; }
  .ob-activate-icon svg { width: 100%; height: 100%; }

  /* Spinner */
  .ob-spinner {
    width: 32px; height: 32px; border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.08); border-top-color: #6366f1;
    animation: spin 0.8s linear infinite;
  }
`;
